import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import TextFieldEditor from '../../ckeditor/text-field.editor';
import { ContentHandlerService } from 'src/app/shared/services/content-handler.service';
import { NgControl } from '@angular/forms';
import { Variable } from 'src/app/shared/types/common';

@Component({
  selector: 'cm-text-input-with-variables',
  templateUrl: './text-input-with-variables.component.html',
  styleUrls: ['../../common-style.scss', './text-input-with-variables.component.scss'],
})
export class TextInputWithVariablesComponent implements AfterViewInit, OnChanges {
  @Input() variables: Variable[] = [];
  @Input() upload: boolean = false;
  @Input() styling: boolean = true;
  @Input() placeholder: string;

  @Output() change = new EventEmitter<string>();

  onChangeFns = new Array<(value: any) => void>();
  onTouchFns = new Array<() => void>();

  @ViewChild('ckEditorContainer')
  ckEditorContainer: ElementRef;

  editor: TextFieldEditor;
  domParser = new DOMParser();

  constructor(@Optional() @Self() private ngControl: NgControl, private contentHandlerService: ContentHandlerService) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.variables) {
      this.editor?.fire('variables:set-variables', this.variables || []);
    }

    if (changes.upload) {
      if (this.upload) {
        this.editor?.fire('file-upload:enable');
      } else {
        this.editor?.fire('file-upload:disable');
      }
    }
  }

  ngAfterViewInit() {
    TextFieldEditor.create(this.ckEditorContainer.nativeElement, {
      placeholder: this.placeholder,
      fileUpload: this.upload
        ? {
            uploaderFn: (file: File) => this.contentHandlerService.uploadFile(file).toPromise(),
          }
        : null,
      variables: this.variables || [],
      styling: this.styling,
    })
      .then((editor) => {
        this.editor = editor;
        this.editor.setData(this.ngControl?.value || '');
        this.editor.model.document.on('change:data', () => {
          const data = this.editor.getData();
          if (this.ngControl && this.ngControl.value !== data) {
            if (this.styling) {
              this.propagateValue(data);
            } else {
              const document = this.domParser.parseFromString(data, 'text/html');
              this.propagateValue(document.body.textContent);
            }
          }

          this.change.emit(this.editor.getData());
        });

        this.editor.ui.focusTracker.on('change:isFocused', (focus) => {
          this.onTouchFns.forEach((fn) => fn());
          this.editor.ui.element.classList.toggle('ck-editor--focused', this.editor.ui.focusTracker.isFocused);
        });
      })
      .catch((error) => {
        console.error(error.stack);
      });
  }

  writeValue(content: string) {
    const data = this.editor?.getData();

    if (this.editor && content !== data) {
      this.editor?.setData(content);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFns.push(fn);
  }

  registerOnTouched(fn: any) {
    this.onTouchFns.push(fn);
  }

  propagateValue(value: any) {
    this.onChangeFns.forEach((fn) => fn(value));
  }

  private getRawText() {
    const div = document.createElement('div');
    div.innerHTML = this.editor.getData();
    const textContent = div.textContent || div.innerText || '';

    // convert non breaking spaces in simple spaces and return
    return textContent.replace(/\s/g, ' ');
  }
}
